import React, { useState } from "react";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import { Table, Button } from "reactstrap";
import { Link } from "react-router-dom";
import {
  Loading, WarningAlert, PaginationControls, ItemsPerPageSelector, SortableHeaderCell, EntityRow, ConfirmationModal,
  NotificationToast, SimpleFormModal, SearchBar, InformationalModal
} from "../components/";
import useSortableData from "../hooks/useSortableData";
import { useEntity } from "../hooks/useEntity";
import { customersApi } from "../api/entitiesApi"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { customerFields, customerDefaultValues } from "../utils/entityFormFields";

// styles
import "../App.css";

export function CustomersComponent() {
  const { data: customers, isLoading, error, setData: setCustomers, setError } = useEntity(customersApi);
  const { items: sortedCustomers, requestSort, sortConfig } = useSortableData(customers, { key: '', direction: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [customersPerPage, setCustomersPerPage] = useState(50);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [currentForm, setCurrentForm] = useState(null);
  const [deleteData, setDeleteData] = useState({ open: false, customerId: null });
  const [notification, setNotification] = useState({ visible: false, title: "", message: "" });
  const [searchTerm, setSearchTerm] = useState("");
  const [errorMessage, setErrorMessage] = useState({ open: false, title: "", message: "" });


  const totalPages = Math.ceil(customers.length / customersPerPage);
  const indexOfLastCustomer = currentPage * customersPerPage;
  const indexOfFirstCustomer = indexOfLastCustomer - customersPerPage;

  const itemsPerPage = [50, 100];

  const filteredCustomers = sortedCustomers.filter(customer => {
    if (!searchTerm) return true;
    const lowerTerm = searchTerm.toLowerCase();
    return (
      (customer.display_name && customer.display_name.toString().toLowerCase().includes(lowerTerm)) ||
      (customer.id && customer.id.toString().toLowerCase().includes(lowerTerm)) ||
      (customer.customer_reference && customer.customer_reference.toString().toLowerCase().includes(lowerTerm)) ||
      (customer.comment && customer.comment.toString().toLowerCase().includes(lowerTerm))
    );
  });
  const currentCustomers = filteredCustomers.slice(indexOfFirstCustomer, indexOfLastCustomer);

  function toggleDropdown() { setDropdownOpen(prevState => !prevState); }
  function handlePageChange(pageNumber) { setCurrentPage(pageNumber); }
  function handleDropdownChange(number) {
    setCustomersPerPage(number);
    setCurrentPage(1);
  };

  function handleEdit(customer) {
    setCurrentForm({ mode: "edit", data: customer });
  };

  function handleAdd() {
    setCurrentForm({ mode: "add", data: {} });
  }

  async function handleSaveForm(formData) {
    try {
      if (currentForm.mode === "edit") {
        await customersApi.updateEntity(formData.id, formData);
        setCustomers(prevData =>
          prevData.map(customer =>
            customer.id === formData.id ? formData : customer
          )
        );
        setNotification({ visible: true, title: "Customer Edited", message: "1 Customer has been edited" });
        setTimeout(() => {
          setNotification({ visible: false, title: "", message: "" });
        }, 2000);
      } else {
        const newCustomer = await customersApi.addEntity(formData);
        setCustomers(prevData => [...prevData, newCustomer]);
        setNotification({ visible: true, title: "Customer Added", message: "1 Customer has been added" });
        setTimeout(() => {
          setNotification({ visible: false, title: "", message: "" });
        }, 2000);
      }
    } catch (err) {
      setErrorMessage({ open: true, title: currentForm.mode === "edit" ? "Error Updating Customer" : "Error Adding Customer", message: err.message });
    } finally {
      setCurrentForm(null);
    }
  }

  function handleCancelFormModal() {
    setCurrentForm(null);
  }

  function openDeleteModal(customer) {
    setDeleteData({ open: true, customerId: customer.id });
  }

  async function confirmDelete() {
    try {
      await customersApi.deleteEntity(deleteData.customerId);
      setCustomers((prevCustomers) =>
        prevCustomers.filter((customer) => customer.id !== deleteData.customerId)
      );
      setNotification({ visible: true, title: "Customer Deleted", message: "1 Customer has been deleted" });
      setTimeout(() => {
        setNotification({ visible: false, title: "", message: "" });
      }, 2000);
    } catch (err) {
      setErrorMessage({ open: true, title: "Error Deleting Customer", message: err.message })
    } finally {
      setDeleteData({ open: false, customerId: null });
    }
  }

  function cancelDelete() {
    setDeleteData({ open: false, customerId: null });
  };

  function dismissInformationalModal() {
    setErrorMessage({ open: false, title: "", message: "" })
  }

  if (isLoading) {
    return <Loading />;
  }

  const displayError = error
    ? error.split("\n").map((line, index) => (
      <React.Fragment key={index}>
        {line}
        <br />
      </React.Fragment>
    ))
    : "Ooops! No customers found";

  return (
    <>
      {customers.length === 0 && (
        <div className="d-flex flex-column align-items-center">
          <WarningAlert message={displayError} />
          <br />
          <div className="d-flex justify-content-center">
            <Button
              color="primary"
              onClick={(e) => {
                e.currentTarget.blur();
                setError(null);
                handleAdd();
              }}
              aria-label={error ? "Create First Customer" : "Create Customer"}
            >
              Create
            </Button>
          </div>
        </div>
      )}

      {customers.length > 0 && (
        <div className="mb-5">
          <div className="d-flex justify-content-end align-items-center mb-2">
            <div style={{ marginRight: "1rem" }}>
              <Button
                color="transparent"
                onClick={(e) => {
                  e.currentTarget.blur();
                  handleAdd();
                }}
                aria-label="Add Customer"
              >
                <FontAwesomeIcon icon="plus" />
              </Button>
            </div>
            <div>
              <SearchBar searchTerm={searchTerm} onSearchChange={setSearchTerm} />
            </div>
          </div>

          {filteredCustomers.length > 0 ? (
            <>
              <Table bordered hover responsive size="sm" striped>
                <thead>
                  <tr>
                    <SortableHeaderCell columnKey={"id"} label={"Id"} currentSort={sortConfig} onSort={requestSort} />
                    <SortableHeaderCell
                      columnKey={"display_name"}
                      label={"Name"}
                      currentSort={sortConfig}
                      onSort={requestSort}
                    />
                    <SortableHeaderCell
                      columnKey={"customer_reference"}
                      label={"Reference"}
                      currentSort={sortConfig}
                      onSort={requestSort}
                    />
                    <SortableHeaderCell
                      columnKey={"comment"}
                      label={"Comment"}
                      currentSort={sortConfig}
                      onSort={requestSort}
                    />
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentCustomers.map((customer) => (
                    <EntityRow
                      key={customer.id}
                      entity={customer}
                      fields={["id", "display_name", "customer_reference", "comment"]}
                      onEdit={handleEdit}
                      onDelete={openDeleteModal}
                      isSelected={
                        (currentForm && currentForm.mode === "edit" && customer.id === currentForm.data.id) ||
                        customer.id === deleteData.customerId
                      }
                      renderField={(field, value) => {
                        if (field === "id") {
                          return <Link to={`/customerDetails/${value}`}>{value.substring(0, 4)}</Link>;
                        } else if(field === "customer_reference") {
                          return <a href={`https://revolta.odoo.com/odoo/contacts/${value}`} target="_blank">{value}</a>;
                        }
                        return value;
                      }}
                    />
                  ))}
                </tbody>
              </Table>
              <div className="d-flex justify-content-between align-items-center">
                <PaginationControls currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
                <ItemsPerPageSelector
                  items={itemsPerPage}
                  currentSelection={customersPerPage}
                  onSelect={handleDropdownChange}
                  isOpen={dropdownOpen}
                  toggle={toggleDropdown}
                />
              </div>
            </>
          ) : (
            <WarningAlert message="Ooops! No customers found" />
          )}
        </div>
      )}


      <InformationalModal
        isOpen={errorMessage.open} title={errorMessage.title} message={errorMessage.message}
        onCancel={dismissInformationalModal}
      />

      <ConfirmationModal
        isOpen={deleteData.open} title="Delete Customer?" message="Are you sure you want to delete this customer?"
        onConfirm={confirmDelete} onCancel={cancelDelete} confirmLabel="OK" cancelLabel="Cancel"
      />

      <SimpleFormModal
        isOpen={!!currentForm} title={currentForm ? (currentForm.mode === "edit" ? "Edit Customer" : "Add Customer") : ""}
        initialData={currentForm ? currentForm.data : null} fields={customerFields()} onSave={handleSaveForm} onCancel={handleCancelFormModal}
        defaultValues={customerDefaultValues}
      />

      {notification.visible && (
        <NotificationToast icon="exclamation-triangle" title={notification.title} message={notification.message} fade={true} />
      )}
    </>
  );
};

export default withAuthenticationRequired(CustomersComponent, {
  onRedirecting: () => <Loading />,
});
